/**
 * Credit Conversion Utilities
 *
 * Helper functions for converting between credits and minutes based on user pricing rates.
 */

/**
 * Convert credits to minutes based on user's pricing rate
 * Rounds down to 1 decimal place for conservative estimates
 *
 * @param credits - The number of credits to convert
 * @param audioRate - The user's credit rate per minute (e.g., 2 for individual free tier)
 * @returns The equivalent minutes, rounded down to 1 decimal place
 */
export const creditsToMinutes = (credits: number, audioRate: number): number => {
  if (audioRate === 0) return 0;
  return Math.floor((credits / audioRate) * 10) / 10;
};

/**
 * Convert minutes to credits (rounds up - ensures user pays enough)
 *
 * @param minutes - The number of minutes to convert
 * @param audioRate - The user's credit rate per minute
 * @returns The equivalent credits, rounded up
 */
export const minutesToCredits = (minutes: number, audioRate: number): number => {
  return Math.ceil(minutes * audioRate);
};

/**
 * Format credits with minutes conversion for display
 *
 * @param credits - The number of credits to display
 * @param audioRate - The user's credit rate per minute
 * @param showRate - Whether to show the rate in the output (default: true)
 * @returns Formatted string like "100 credits (~50 min at 2 credits/min)" or "100 credits (~50 min)"
 *
 * @example
 * formatCreditsWithMinutes(100, 2, true)
 * // Returns: "100 credits (~50 min at 2 credits/min)"
 *
 * @example
 * formatCreditsWithMinutes(100, 1.5, false)
 * // Returns: "100 credits (~66.6 min)"
 */
export const formatCreditsWithMinutes = (
  credits: number,
  audioRate: number,
  showRate: boolean = true
): string => {
  const minutes = creditsToMinutes(credits, audioRate);

  if (showRate) {
    return `${credits} credits (~${minutes} min at ${audioRate} credits/min)`;
  }
  return `${credits} credits (~${minutes} min)`;
};
